import { useState, useEffect } from "react";
import { useDisclosure } from "@chakra-ui/react";

import MetaHead from "components/base/MetaHead";
import Button from "components/base/Button";

import MainContainer from "components/sections/MainContainer";
import FloatingCTA from "components/blocks/FloatingCTA";
import HeroHeader from "components/sections/HeroHeader";
import ModalVideo from "components/sections/ModalVideo";
import EpisodesSection from "components/sections/EpisodesSection";

import { allowedLocales } from "translations";
import Trans from "components/base/Trans";
import { useRouter } from "next/router";
import { getAllEpisodes, getEpisodesPage } from "lib/payloadApi";

export default function Docuseries({ initialEpisodes }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const router = useRouter();
  const [videoId, setVideoId] = useState(null);
  const [{ allEpisodes, page, loading }, setPageState] = useState({
    allEpisodes: initialEpisodes,
    page: 1,
    loading: false,
  });

  useEffect(() => {
    if (router.query.id) {
      const found = allEpisodes.results.find((e) => e.uid === router.query.id);
      if (found) {
        setVideoId(found.data.feature_video);
        onOpen();
      }
    }
  }, [allEpisodes, router.query.id]);

  const loadMore = () => {
    setPageState({ allEpisodes, page: page + 1, loading: true });
  };

  useEffect(() => {
    const selectedLocale =
      allowedLocales.indexOf(router.locale) > -1 ? router.locale : null;

    getEpisodesPage(1, selectedLocale).then((newPage) => {
      setPageState({ allEpisodes: newPage, page: 1, loading: false });
    });
  }, [router.locale]);

  useEffect(() => {
    if (loading) {
      getEpisodesPage(page, router.locale === "en" ? null : router.locale).then(
        (newPage) => {
          const newEpisodesObject = Object.assign(allEpisodes, {
            results: allEpisodes.results.concat(newPage.results),
            page,
          });

          setPageState({
            allEpisodes: newEpisodesObject,
            page,
            loading: false,
          });
        },
      );
    }
  }, [page]);

  return (
    <MainContainer
      navStyle={{ gradient: true, inverted: false }}
      bg="black.900"
    >
      <MetaHead
        title="LBL_DOCUSERIES_META_TITLE"
        description="LBL_DOCUSERIES_META_DESC"
        imageUrl="/images/meta/blog.jpg"
      />
      <HeroHeader
        height="80vh"
        imageSrc={{
          base: "/images/docuseries-header-mobile.jpg",
          md: "/images/docuseries-header.jpg",
        }}
        title={<Trans id="LBL_DOCUSERIES_TITLE" />}
        subtitle={<Trans id="LBL_DOCUSERIES_SUBTITLE" />}
        description={<Trans id="LBL_DOCUSERIES_DESCRIPTION" />}
        descriptionType="p_md"
        noScrollHint
        cta={
          <Button
            onClick={() => {
              document
                .getElementById("presentation")
                .scrollIntoView({ behavior: "smooth" });
            }}
            trackLabel="docuseries_watch_episodes"
            mt={[4, null, 12]}
            mr={[2, 4, 6]}
          >
            <Trans id="LBL_DOCUSERIES_CTA" />
          </Button>
        }
      />
      <EpisodesSection
        allEpisodes={allEpisodes}
        loading={loading}
        loadMore={loadMore}
        onEpisodeClick={(id, uid) => {
          setVideoId(id);
          onOpen();
          router.push(`/docuseries?id=${uid}`, undefined, {
            shallow: true,
          });
        }}
      />
      <ModalVideo
        isOpen={isOpen}
        onClose={() => {
          router.push(`/docuseries`, undefined, { shallow: true });
          onClose();
        }}
        videoId={videoId}
      />
      <FloatingCTA />
    </MainContainer>
  );
}

export async function getStaticProps({ locale }) {
  const selectedLocale = allowedLocales.indexOf(locale) > -1 ? locale : null;

  const allEpisodes = await getAllEpisodes(selectedLocale);

  return {
    props: { initialEpisodes: allEpisodes },
    revalidate: 60,
  };
}
